import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Copy, CheckCircle, Download, Loader2, Sparkles, FileText, Bookmark,
  Edit3, PlayCircle, ListChecks, ArrowRight,
} from 'lucide-react';
import { downloadMd, slugifyClient, maskContent, isVideoItem } from './skillsUtils';

const contentStyle = {
  background: 'var(--dp-bg-glass, rgba(28,28,28,0.85))',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  border: '1px solid var(--dp-border-medium)',
  borderRadius: '16px',
  boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
  maxWidth: '840px',
  maxHeight: '88vh',
  overflow: 'auto',
  padding: '24px',
};

const sectionLabelStyle = {
  display: 'flex', alignItems: 'center', gap: '6px',
  fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px',
  textTransform: 'uppercase', color: 'var(--dp-text-tertiary)',
  fontFamily: 'var(--dp-font)', marginBottom: '8px',
};

/**
 * Detalhe de skill (sistema e Minhas Skills): instruções de uso, tutorial em
 * vídeo, imagens com badge do gerador e conteúdo integral.
 * `getDetail(skill)` resolve {conteudo, instrucoes, video_url} (fetch p/
 * sistema, imediato p/ skill do usuário).
 */
const SkillDetailModal = ({
  skill, variant = 'system', onClose, getDetail, onSave, onEdit, onCopied,
  canalLabel = '', isMobile = false, isPrivate = false,
}) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null); // {conteudo, instrucoes, video_url}
  const [loadError, setLoadError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    if (!skill) return;
    let alive = true;
    setDetail(null);
    setLoadError(false);
    setCopied(false);
    setActiveImg(0);
    Promise.resolve(getDetail(skill))
      .then((d) => { if (alive) setDetail(d || {}); })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, [skill?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!skill) return null;

  const HeaderIcon = variant === 'user' ? FileText : Sparkles;
  const conteudo = detail?.conteudo;
  const instrucoes = detail?.instrucoes;
  const videoUrl = detail?.video_url;
  const displayContent = conteudo != null && isPrivate && variant === 'user'
    ? maskContent(conteudo)
    : conteudo;

  const handleCopy = async () => {
    if (conteudo == null) return;
    try {
      await navigator.clipboard.writeText(conteudo);
      setCopied(true);
      toast.success(t('skills.detail.copied'));
      onCopied?.(skill);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error(t('skills.detail.error_copy'));
    }
  };

  const handleDownload = () => {
    if (conteudo == null) return;
    const base = variant === 'user' ? slugifyClient(skill.nome) : (skill.slug || slugifyClient(skill.nome));
    downloadMd(`${base}.md`, conteudo);
  };

  const actionBtnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '8px 14px', borderRadius: '9px',
    fontSize: '12.5px', fontWeight: 600, fontFamily: 'var(--dp-font)',
    cursor: 'pointer', transition: 'all 0.2s ease', lineHeight: 1.4,
    border: '1px solid var(--dp-border-medium)',
    background: 'var(--dp-bg-elevated)',
    color: 'var(--dp-text-primary)',
  };

  return (
    <Dialog open={!!skill} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent style={contentStyle}>
        <DialogHeader>
          <DialogTitle style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            fontSize: '17px', fontWeight: 700, color: 'var(--dp-text-primary)',
            fontFamily: 'var(--dp-font)',
          }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '34px', height: '34px', borderRadius: '9px', flexShrink: 0,
              background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
              boxShadow: '0 4px 14px rgba(13, 174, 255, 0.28)',
            }}>
              <HeaderIcon size={17} color="#FFFFFF" strokeWidth={2.2} />
            </span>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{skill.nome}</span>
          </DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Meta: canal + categoria + tags */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {canalLabel && (
              <span style={{
                padding: '3px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600,
                fontFamily: 'var(--dp-font)', color: 'var(--dp-blue-500)',
                background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.22)',
              }}>
                {canalLabel}
              </span>
            )}
            {skill.categoria && (
              <span style={{
                padding: '3px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 500,
                fontFamily: 'var(--dp-font)', color: 'var(--dp-text-secondary)',
                background: 'var(--dp-bg-elevated)', border: '1px solid var(--dp-border-subtle)',
              }}>
                {skill.categoria}
              </span>
            )}
            {(skill.plataformas || []).map((p) => (
              <span key={`plat-${p}`} style={{
                padding: '3px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                fontFamily: 'var(--dp-font)', color: 'var(--dp-text-secondary)',
                background: 'var(--dp-bg-elevated)', border: '1px solid var(--dp-border-subtle)',
              }}>
                {p}
              </span>
            ))}
            {(skill.tags || []).slice(0, 6).map((tag) => (
              <span key={tag} style={{
                padding: '3px 10px', borderRadius: '9999px', fontSize: '11px',
                fontFamily: 'var(--dp-font)', color: 'var(--dp-text-tertiary)',
                border: '1px solid var(--dp-border-subtle)',
              }}>
                {tag}
              </span>
            ))}
            {variant === 'system' && (skill.copies_count || 0) > 0 && (
              <span style={{
                fontSize: '11px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
              }}>
                {t('skills.card.copies', { count: skill.copies_count })}
              </span>
            )}
          </div>

          {skill.descricao && (
            <p style={{
              margin: 0, fontSize: '13px', color: 'var(--dp-text-secondary)',
              fontFamily: 'var(--dp-font)', lineHeight: 1.55,
            }}>
              {skill.descricao}
            </p>
          )}

          {/* Tutorial em vídeo (link normal, ex.: YouTube — abre em nova aba) */}
          {videoUrl && (
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '11px 14px', borderRadius: '10px', textDecoration: 'none',
                border: '1px solid rgba(13, 174, 255, 0.25)',
                background: 'rgba(13, 174, 255, 0.06)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13, 174, 255, 0.11)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13, 174, 255, 0.06)'; }}
            >
              <PlayCircle size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)', flexShrink: 0 }} />
              <span style={{
                flex: 1, fontSize: '13px', fontWeight: 600,
                color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
              }}>
                {t('skills.detail.tutorial')}
              </span>
              <ArrowRight size={14} style={{ color: 'var(--dp-blue-500, #0DAEFF)', flexShrink: 0 }} />
            </a>
          )}

          {/* Instruções de uso */}
          {(instrucoes || (detail == null && !loadError)) && (
            <div>
              <span style={sectionLabelStyle}>
                <ListChecks size={12} />
                {t('skills.detail.how_to_use')}
              </span>
              {detail == null && !loadError ? (
                <div className="dp-skeleton" style={{ height: '52px', borderRadius: '10px' }} />
              ) : instrucoes ? (
                <div style={{
                  padding: '12px 14px', borderRadius: '10px',
                  border: '1px solid var(--dp-border-subtle)', background: 'var(--dp-bg-elevated)',
                  color: 'var(--dp-text-primary)', fontSize: '12.5px', lineHeight: 1.6,
                  fontFamily: 'var(--dp-font)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {instrucoes}
                </div>
              ) : null}
            </div>
          )}

          {/* Galeria de EXEMPLOS (imagens E vídeos upados, juntos): mídia
              GRANDE inteira (sem corte) + miniaturas clicáveis. Vídeo toca
              embutido com controles. */}
          {(skill.imagens || []).length > 0 && (() => {
            const imgs = skill.imagens;
            const cur = Math.min(activeImg, imgs.length - 1);
            const curIsVideo = isVideoItem(imgs[cur]);
            return (
              <div>
                <span style={sectionLabelStyle}>{t('skills.detail.images')}</span>
                <div style={{
                  position: 'relative', width: '100%', aspectRatio: '16 / 9',
                  borderRadius: '12px', overflow: 'hidden',
                  border: '1px solid var(--dp-border-subtle)',
                  background: '#000000',
                }}>
                  {curIsVideo ? (
                    <video
                      key={imgs[cur].url}
                      src={imgs[cur].url}
                      controls
                      preload="metadata"
                      playsInline
                      style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        objectFit: 'contain', display: 'block', background: '#000000',
                      }}
                    />
                  ) : (
                  <img
                    src={imgs[cur].url}
                    alt={imgs[cur].label || skill.nome}
                    onError={(e) => { e.target.style.display = 'none'; }}
                    style={{
                      position: 'absolute', inset: 0, width: '100%', height: '100%',
                      objectFit: 'contain', display: 'block',
                    }}
                  />
                  )}
                  {imgs[cur].label && !curIsVideo && (
                    <span style={{
                      position: 'absolute', left: '10px', bottom: '10px',
                      padding: '4px 11px', borderRadius: '9999px',
                      fontSize: '11.5px', fontWeight: 600, fontFamily: 'var(--dp-font)',
                      color: '#F5F5F5', background: 'rgba(0, 0, 0, 0.65)',
                      backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 255, 255, 0.14)',
                    }}>
                      {imgs[cur].label}
                    </span>
                  )}
                  {/* Contador no topo p/ vídeo (embaixo cobriria os controles) */}
                  {imgs.length > 1 && (
                    <span style={{
                      position: 'absolute', right: '10px',
                      ...(curIsVideo ? { top: '10px' } : { bottom: '10px' }),
                      padding: '4px 10px', borderRadius: '9999px',
                      fontSize: '11px', fontWeight: 600, fontFamily: 'var(--dp-font)',
                      color: '#F5F5F5', background: 'rgba(0, 0, 0, 0.65)',
                      backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 255, 255, 0.14)',
                    }}>
                      {cur + 1}/{imgs.length}
                    </span>
                  )}
                </div>
                {imgs.length > 1 && (
                  <div style={{
                    display: 'flex', gap: '8px', marginTop: '8px',
                    overflowX: 'auto', paddingBottom: '2px',
                  }}>
                    {imgs.map((img, i) => (
                      <button
                        key={`${img.url}-${i}`}
                        type="button"
                        onClick={() => setActiveImg(i)}
                        title={img.label || ''}
                        style={{
                          position: 'relative', width: '96px', aspectRatio: '16 / 9',
                          borderRadius: '8px', overflow: 'hidden', flexShrink: 0,
                          padding: 0, cursor: 'pointer',
                          border: i === cur
                            ? '2px solid var(--dp-blue-500, #0DAEFF)'
                            : '1px solid var(--dp-border-subtle)',
                          background: 'var(--dp-bg-elevated)',
                          opacity: i === cur ? 1 : 0.65,
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={(e) => { if (i !== cur) e.currentTarget.style.opacity = 1; }}
                        onMouseLeave={(e) => { if (i !== cur) e.currentTarget.style.opacity = 0.65; }}
                      >
                        {isVideoItem(img) ? (
                          <>
                            <video
                              src={img.url}
                              muted
                              preload="metadata"
                              style={{
                                position: 'absolute', inset: 0, width: '100%', height: '100%',
                                objectFit: 'cover', display: 'block', background: '#000',
                              }}
                            />
                            <span style={{
                              position: 'absolute', inset: 0, display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              background: 'rgba(0, 0, 0, 0.25)', pointerEvents: 'none',
                            }}>
                              <PlayCircle size={20} color="#FFFFFF" />
                            </span>
                          </>
                        ) : (
                        <img
                          src={img.url}
                          alt={img.label || ''}
                          loading="lazy"
                          onError={(e) => { e.target.style.display = 'none'; }}
                          style={{
                            position: 'absolute', inset: 0, width: '100%', height: '100%',
                            objectFit: 'cover', display: 'block',
                          }}
                        />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Conteúdo markdown */}
          <div>
            <span style={sectionLabelStyle}>{t('skills.detail.content')}</span>
            {conteudo == null && !loadError ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '18px 14px',
                borderRadius: '10px', border: '1px solid var(--dp-border-subtle)',
                background: 'var(--dp-bg-input)', color: 'var(--dp-text-secondary)',
                fontSize: '12.5px', fontFamily: 'var(--dp-font)',
              }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                {t('skills.detail.loading')}
              </div>
            ) : loadError ? (
              <div style={{
                padding: '18px 14px', borderRadius: '10px',
                border: '1px solid rgba(248, 113, 113, 0.3)', background: 'rgba(248, 113, 113, 0.06)',
                color: 'var(--dp-red-500, #F87171)', fontSize: '12.5px', fontFamily: 'var(--dp-font)',
              }}>
                {t('skills.detail.error_load')}
              </div>
            ) : (
              <pre style={{
                margin: 0, padding: '14px', borderRadius: '10px',
                border: '1px solid var(--dp-border-subtle)', background: 'var(--dp-bg-input)',
                color: 'var(--dp-text-primary)', fontSize: '12px', lineHeight: 1.55,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: '40vh', overflow: 'auto',
              }}>
                {displayContent}
              </pre>
            )}
          </div>

          {/* Ações */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleCopy}
              disabled={conteudo == null}
              style={{
                ...actionBtnStyle,
                opacity: conteudo == null ? 0.5 : 1,
                cursor: conteudo == null ? 'not-allowed' : 'pointer',
                color: copied ? 'var(--dp-green-500, #34D399)' : 'var(--dp-text-primary)',
                borderColor: copied ? 'rgba(52, 211, 153, 0.4)' : 'var(--dp-border-medium)',
              }}
            >
              {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
              {t('skills.detail.copy')}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={conteudo == null}
              style={{
                ...actionBtnStyle,
                opacity: conteudo == null ? 0.5 : 1,
                cursor: conteudo == null ? 'not-allowed' : 'pointer',
              }}
            >
              <Download size={14} />
              {t('skills.detail.download')}
            </button>
            <span style={{ flex: 1 }} />
            {variant === 'system' && onSave && (
              <button
                type="button"
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={() => { onSave(skill); onClose(); }}
              >
                <Bookmark size={14} />
                {t('skills.card.save_long')}
                <ArrowRight size={14} />
              </button>
            )}
            {variant === 'user' && onEdit && (
              <button
                type="button"
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={() => { onEdit(skill); onClose(); }}
              >
                <Edit3 size={14} />
                {t('skills.admin.edit_skill')}
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SkillDetailModal;
